/**
 * RegionSelect — a reusable SA-province dropdown for the geo-region MVP (web).
 *
 * One small `@fse/ui` (MUI) Select bound to the 9 canonical provinces from
 * `lib/regions.ts`, plus a leading blank option whose label the caller supplies
 * (e.g. "All regions" for the buyer browse, "Not set" for the farm editor).
 * Reused by the marketplace browse header (buyer "near me" picker) and the
 * farm-profile editor (the farmer's own region).
 *
 * Value contract: the selected value is a `SaProvince` or null (the blank
 * option). It mirrors the existing grade Select in _app.create-listing.tsx —
 * an empty string ('') in the MUI value maps to/from null at this boundary so
 * the rest of the app deals only in `SaProvince | null`.
 */
import { FormControl, InputLabel, MenuItem, Select } from '@fse/ui';
import { useId } from 'react';

import { SA_PROVINCES, normalizeRegion, type SaProvince } from '../lib/regions';

export interface RegionSelectProps {
  /** Field label (above the control), e.g. "Region". */
  label: string;
  /** The selected province, or null for the blank option. */
  value: SaProvince | null;
  /** Fired with the new province, or null when the blank option is chosen. */
  onChange: (next: SaProvince | null) => void;
  /** Label for the leading blank option (e.g. "All regions" / "Not set"). */
  blankLabel: string;
  /** Disable the control (e.g. while saving). */
  disabled?: boolean;
  /** Stretch to the container width. Default true. */
  fullWidth?: boolean;
  /** MUI size. Default "medium" (the browse header passes "small"). */
  size?: 'small' | 'medium';
}

export function RegionSelect({
  label,
  value,
  onChange,
  blankLabel,
  disabled,
  fullWidth = true,
  size = 'medium',
}: RegionSelectProps) {
  // Stable, unique id so the label associates with the control (a11y) even when
  // several RegionSelects render on one page.
  const labelId = useId();

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled} size={size}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(normalizeRegion(e.target.value))}
      >
        <MenuItem value="">{blankLabel}</MenuItem>
        {SA_PROVINCES.map((p) => (
          <MenuItem key={p} value={p}>
            {p}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
